import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { List, Checkbox as CheckboxAntD } from "antd";
import classnames from "classnames";
import { connect } from "formik";
import { Checkbox } from "formik-antd";
import { each, first, get, has, isEmpty, omit } from "lodash";
import { formFieldIsInErrorState } from "../../utils/formikUtils";
import useUpdateEffect from "../../utils/useUpdateEffect";
import FormFieldFormItem from "./FormFieldFormItem";
import FormFieldReadOnlyContent from "./FormFieldReadOnlyFields/FormFieldReadOnlyContent";
import ReadOnlyList from "./FormFieldReadOnlyFields/ReadOnlyList";
import "./FormFieldCheckboxGroup.scss";

function renderOptionsList(options) {
  return (
    <List
      itemLayout="horizontal"
      dataSource={options}
      renderItem={option => {
        return (
          <List.Item>
            <CheckboxAntD value={option.value} disabled={option.disabled}>
              {option.label}
            </CheckboxAntD>
          </List.Item>
        );
      }}
    />
  );
}

function FormFieldCheckboxGroup(props) {
  const { name, meta, disabled, style, formik, readOnly } = props;

  const options = get(meta, "options", []);
  const displayDefaultLabel = get(meta, "displayDefaultLabel", true);
  const allowSelectAll = get(meta, "allowSelectAll", false);
  const selectAllLabel = get(meta, "selectAllLabel", "All Competitions");

  const { values, errors, touched, submitCount, setFieldValue, setFieldTouched } = formik;

  const fieldValue = get(values, name, null);
  const selectedValues = useMemo(() => (Array.isArray(fieldValue) ? fieldValue : []), [fieldValue]);

  const activeOptionValues = useMemo(() => {
    const renderOptionGroups = has(first(options), "children");

    if (!renderOptionGroups) {
      return options.filter(option => !option.disabled).map(option => option.value);
    }

    const result = [];
    each(options, optionGroup => {
      each(optionGroup.children, option => {
        if (!option.disabled) {
          result.push(option.value);
        }
      });
    });
    return result;
  }, [options]);

  const isAllSelected = useMemo(() => {
    if (activeOptionValues.length === 0) {
      return false;
    }
    return activeOptionValues.every(val => selectedValues.includes(val));
  }, [activeOptionValues, selectedValues]);

  const [isAllMode, setIsAllMode] = useState(isAllSelected);

  // Sync isAllMode whenever selection changes
  useUpdateEffect(() => {
    setIsAllMode(isAllSelected);
  }, [isAllSelected]);

  // If "All" was selected and additional competitions are added before report generation,
  // automatically include newly added competitions
  useUpdateEffect(() => {
    if (isAllMode && activeOptionValues.length > 0) {
      setFieldValue(name, activeOptionValues);
    }
  }, [activeOptionValues]);

  const handleToggleAll = useCallback(
    e => {
      const { checked } = e.target;
      setFieldTouched(name, true);
      if (checked) {
        setIsAllMode(true);
        setFieldValue(name, activeOptionValues);
      } else {
        setIsAllMode(false);
        setFieldValue(name, []);
      }
    },
    [name, activeOptionValues, setFieldValue, setFieldTouched],
  );

  const isInErrorState = formFieldIsInErrorState(name, errors, touched, submitCount);
  const showSelectAll = allowSelectAll && options.length > 0;

  const containerClassName = classnames("form-field-checkbox-group__container", {
    "form-field-checkbox-group__container--error": isInErrorState,
  });

  const checkboxGroupClassName = classnames("form-field-checkbox-group__checkbox-group", {
    "form-field-checkbox-group__checkbox-group--error": isInErrorState && !showSelectAll,
    "form-field-checkbox-group__checkbox-group--with-select-all": showSelectAll,
  });

  function renderOptions() {
    const renderOptionGroups = has(first(options), "children");

    if (!renderOptionGroups) {
      return renderOptionsList(options);
    }

    return options.map(optionGroup => {
      return (
        <div key={optionGroup.key}>
          <div className="ant-select-dropdown-menu-item-group-title">{optionGroup.label}</div>
          {renderOptionsList(optionGroup.children, fieldValue)}
        </div>
      );
    });
  }

  function getReadOnlyContent() {
    if (isEmpty(fieldValue)) {
      return null;
    }
    return <ReadOnlyList options={options} selectedValues={fieldValue} />;
  }

  if (readOnly) {
    return <FormFieldReadOnlyContent name={name} meta={meta} content={getReadOnlyContent()} />;
  }

  const renderCheckboxGroup = () => (
    <Checkbox.Group name={name} disabled={disabled} className={checkboxGroupClassName} style={style}>
      {renderOptions()}
    </Checkbox.Group>
  );

  return (
    <FormFieldFormItem
      {...omit(props, ["formik"])}
      className="form-field-checkbox-group"
      displayDefaultLabel={displayDefaultLabel}
      displayForInput={false}
    >
      {showSelectAll ? (
        <div className={containerClassName}>
          <div className="form-field-checkbox-group__select-all">
            <CheckboxAntD
              checked={isAllSelected}
              onChange={handleToggleAll}
              disabled={disabled || activeOptionValues.length === 0}
            >
              {selectAllLabel}
            </CheckboxAntD>
          </div>
          {renderCheckboxGroup()}
        </div>
      ) : (
        renderCheckboxGroup()
      )}
    </FormFieldFormItem>
  );
}

FormFieldCheckboxGroup.propTypes = {
  name: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  formik: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

FormFieldCheckboxGroup.defaultProps = {
  disabled: false,
  readOnly: false,
  style: {},
};

export default connect(FormFieldCheckboxGroup);
